import React, { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useIntl } from 'react-intl'
import { toast } from 'react-toastify'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableFooter from '@mui/material/TableFooter'
import TablePagination from '@mui/material/TablePagination'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import IconButton from '@mui/material/IconButton'
import { LinearProgress, TableHead } from '@mui/material'
import AddIcon from '../../../../assets/icons/Add.svg'
import { courseTopicApiSlice } from '../../../../features/course/courseTopicSlice'
import TablePaginationActions from '../../../../components/muiComponents/tablePaginationActions/TablePaginationActions'
import { LANGUAGES } from '../../../../utils/constants'
import { TopicState } from '../../../../features/types'

interface Props {
    inactiveCourseTopicsData: TopicState[]
    inactiveFetching: boolean
    inactiveTopicsPage: number
    inactiveTopicsRowsPerPage: number
    setInactiveTopicsPage: (page: number) => void
    setInactiveTopicsRowsPerPage: (rowsPerPage: number) => void
}

const InactiveCourseTopicsTable: React.FC<Props> = ({
    inactiveCourseTopicsData = [],
    inactiveFetching = false,
    inactiveTopicsPage,
    inactiveTopicsRowsPerPage,
    setInactiveTopicsPage,
    setInactiveTopicsRowsPerPage,
}) => {
    const { formatMessage } = useIntl()
    const [createCourseTopic, { isLoading: isCreating }] =
        courseTopicApiSlice.useCreateCourseTopicMutation()
    const { courseSubjectId } = useParams()
    const [anchorEl, setAnchorEl] = useState(null)
    const [topicId, setTopicId] = useState<number>(null)
    // Avoid a layout jump when reaching the last page with empty rows.
    const emptyRows =
        inactiveTopicsPage > 0
            ? Math.max(
                  0,
                  inactiveTopicsRowsPerPage - inactiveCourseTopicsData.length,
              )
            : 0

    const handleChangePage = (
        event: React.MouseEvent<HTMLButtonElement> | null,
        newPage: number,
    ) => {
        setInactiveTopicsPage(newPage + 1)
    }

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        setInactiveTopicsRowsPerPage(parseInt(event.target.value, 10))
        setInactiveTopicsPage(1)
    }

    const addActiveTopic = async (languageEnum: string) => {
        try {
            await createCourseTopic({
                courseSubjectId,
                topicId,
                languageEnum,
            }).unwrap()
            setAnchorEl(null)
            toast.success(formatMessage({ id: 'savedSuccess' }))
        } catch (error) {}
    }

    const showLanguages = (event, row: TopicState) => {
        setAnchorEl(event.currentTarget)
        setTopicId(row.id)
    }

    function hideLanguages() {
        setAnchorEl(null)
    }

    return (
        <TableContainer
            component={Paper}
            sx={{ border: 1, borderColor: 'grey.100', borderRadius: 2 }}
            elevation={0}
        >
            {isCreating && <LinearProgress />}

            <Table
                sx={{ minWidth: 500 }}
                aria-label="custom pagination table"
            >
                <TableHead sx={{ bgcolor: 'grey.100' }}>
                    <TableRow>
                        <TableCell>#</TableCell>

                        <TableCell>
                            {formatMessage({ id: 'titleKz' })}
                        </TableCell>

                        <TableCell>
                            {formatMessage({ id: 'titleRu' })}
                        </TableCell>

                        <TableCell align="left">
                            {formatMessage({ id: 'actions' })}
                        </TableCell>
                    </TableRow>
                </TableHead>

                {inactiveCourseTopicsData.length ? (
                    <TableBody>
                        {(inactiveTopicsRowsPerPage > 0
                            ? inactiveCourseTopicsData.slice(
                                  (inactiveTopicsPage - 1) *
                                      inactiveTopicsRowsPerPage,
                                  (inactiveTopicsPage - 1) *
                                      inactiveTopicsRowsPerPage +
                                      inactiveTopicsRowsPerPage,
                              )
                            : inactiveCourseTopicsData
                        ).map((row, index) => (
                            <TableRow key={row.id}>
                                <TableCell
                                    component="th"
                                    scope="row"
                                >
                                    {index + 1}
                                </TableCell>

                                <TableCell
                                    component="th"
                                    scope="row"
                                >
                                    {row?.titleKz}
                                </TableCell>

                                <TableCell
                                    component="th"
                                    scope="row"
                                >
                                    {row?.titleRu}
                                </TableCell>

                                <TableCell
                                    style={{ width: 60 }}
                                    align="left"
                                >
                                    <IconButton
                                        aria-controls="composition-menu"
                                        size="small"
                                        disabled={isCreating}
                                        aria-owns={
                                            anchorEl
                                                ? 'composition-menu'
                                                : undefined
                                        }
                                        aria-haspopup="true"
                                        onClick={(event) =>
                                            showLanguages(event, row)
                                        }
                                    >
                                        <img
                                            src={AddIcon}
                                            alt="add"
                                            style={{ cursor: 'pointer' }}
                                        />
                                    </IconButton>

                                    <Menu
                                        id="composition-menu"
                                        anchorEl={anchorEl}
                                        keepMounted
                                        open={Boolean(anchorEl)}
                                        onClose={hideLanguages}
                                        elevation={1}
                                    >
                                        {LANGUAGES({ formatMessage }).map(
                                            (item) => (
                                                <MenuItem
                                                    key={item.id}
                                                    onClick={() =>
                                                        addActiveTopic(item.id)
                                                    }
                                                >
                                                    {item.name}
                                                </MenuItem>
                                            ),
                                        )}

                                        <MenuItem
                                            onClick={() => addActiveTopic(null)}
                                        >
                                            KZ,RU
                                        </MenuItem>
                                    </Menu>
                                </TableCell>
                            </TableRow>
                        ))}

                        {emptyRows > 0 && (
                            <TableRow style={{ height: 53 * emptyRows }}>
                                <TableCell colSpan={6} />
                            </TableRow>
                        )}
                    </TableBody>
                ) : (
                    !inactiveFetching && (
                        <TableBody>
                            <TableRow>
                                <TableCell
                                    component="th"
                                    scope="row"
                                    align="center"
                                    colSpan={4}
                                >
                                    {formatMessage({ id: 'noData' })}
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    )
                )}

                <TableFooter>
                    <TableRow>
                        <TablePagination
                            rowsPerPageOptions={[
                                5,
                                10,
                                25,
                                { label: 'All', value: -1 },
                            ]}
                            colSpan={4}
                            count={inactiveCourseTopicsData.length}
                            rowsPerPage={inactiveTopicsRowsPerPage}
                            page={inactiveTopicsPage - 1}
                            SelectProps={{
                                inputProps: {
                                    'aria-label': 'rows per page',
                                },
                                native: true,
                            }}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            ActionsComponent={TablePaginationActions}
                            sx={{ border: 0 }}
                        />
                    </TableRow>
                </TableFooter>
            </Table>
        </TableContainer>
    )
}

export default InactiveCourseTopicsTable
